<template>
    <div class="h-full">
        <banner
            v-if="error"
            :description="error"
            warning
            inline
        />

        <div :class="[ topImage ? '' : 'border-2 border-primary-20 border-dashed cursor-pointer' , classes ]"
             class="overflow-hidden relative group rounded"
             @dragend="handleDragStop"
             @dragenter="handleDragEnter"
             @dragleave="handleDragStop"
             @dragover.prevent
             @drop.prevent="handleDrop"
             @click="$refs['img-' + _uid].click()"
        >

            <div class="pointer-events-none w-full h-full">

                <!-- <transition name="fade"> -->
                <div v-if="topImage" class="w-full h-full">
                    <img
                        alt="image"
                        class="w-full h-full"
                        :class="isSquare ? 'object-contain' : 'object-cover'"
                        :src="topImage"
                    >
                </div>
                <!-- </transition> -->

                <transition name="fade-quick">

                    <!-- The edit button -->
                    <div v-if="canDrop === null && !uploading" key="1" class="absolute z-10 inset-0 flex items-center justify-center pointer-events-none p-16">
                        <div
                            v-show="this.topImage"
                            :class="[ iconLocation === 'center' ? 'items-center justify-center' : 'items-start justify-between' ]"
                            class="w-full h-full flex"
                        >
                            <div
                                class="flex items-center gap-4"
                                @click.stop
                            >
                                <simple-button
                                    class="pointer-events-auto"
                                    icon="photo-camera"
                                    look="icon"
                                    @click="$refs['img-' + _uid].click()"
                                />
                                <simple-button
                                    class="pointer-events-auto"
                                    icon="delete"
                                    look="icon"
                                    v-if="!required"
                                    @click="clearImage"
                                />
                            </div>

                            <div @click.stop>
                                <more-info-button
                                    v-if="ratio"
                                    only-use-icon
                                    hide-label
                                    position="right"
                                    class="ml-auto pointer-events-auto cursor-pointer"
                                >
                                    <template #content>
                                        <b class="block mb-4">{{ $t('general.more_info') }}</b>
                                        <p v-html="$t('general.ideal_ratio', {ratio: ratio})"></p>
                                    </template>
                                </more-info-button>
                            </div>
                        </div>
                        <div
                            v-show="!this.topImage"
                            :class="[ isSmall ? 'text-body-sm' : 'text-body-sm' ]"
                            class="w-full h-full flex items-center justify-center p-16 text-center"
                        >
                            <div>
                                <div v-if="!isSmall" class="mb-16 text-primary-80">{{ $t('general.uploads.drop_photo') }}</div>
                                <svg-icon :class="[ isSmall ? 'w-24 h-16 mb-8' : 'w-48 h-32 mb-16' ]"
                                          class="mx-auto fill-primary-80"
                                          type="upload"
                                />
                                <div
                                    class="inline-flex items-center gap-4"
                                    @click.stop
                                >
                                    <span v-if="!isSmall">{{ $t('general.or') }}</span>
                                    <simple-button
                                        class="pointer-events-auto"
                                        display="secondary"
                                        look="text"
                                        @click="$refs['img-' + _uid].click()"
                                    >
                                        {{ $t('general.uploads.choose_photo') }}
                                    </simple-button>
                                </div>
                            </div>
                        </div>
                    </div>


                    <!-- The drag indicators -->
                    <div
                        v-else
                        key="2"
                        :class="[ canDrop ? 'text-primary' : 'text-semantic-error' ]"
                        class="w-full h-full relative flex items-center justify-center"
                    >
                        <div class="relative z-10 flex flex-wrap justify-center">
                            <loading v-if="uploading" class="w-full" size="small"></loading>
                            <svg-icon v-else-if="!canDrop" class="w-32 h-32 mx-8" type="forbidden-large"></svg-icon>
                            <svg-icon v-else ref="dropIndicator" class="w-48 h-32 mx-8" type="upload"></svg-icon>
                        </div>
                        <div class="absolute inset-0 bg-white w-full h-full opacity-75"></div>
                    </div>

                </transition>

            </div>

            <input :id="'img-' + _uid" :ref="'img-' + _uid" accept="image/*" class="invisible" type="file" @change="handleInput">

        </div>
    </div>
</template>

<script>
import SvgIcon from '../ui/SvgIcon.vue';
import Loading from '../ui/Loading.vue';
import * as files from '../../services/FileService';
import MoreInfoButton from '../ui/MoreInfoButton';
import Banner from "../ui/Banner.vue";
import SimpleButton from "../ui/SimpleButton.vue";

export default {
    components: {
        SimpleButton,
        Banner,
        MoreInfoButton,
        'svg-icon': SvgIcon,
        'loading': Loading,
    },
    props: {
        classes: String,
        image: String,
        isSmall: Boolean,
        isSquare: Boolean,
        iconLocation: {
            type: String,
            default: 'center',
            validator: value => ['center', 'corner'].includes(value),
        },
        required: Boolean,
        value: {
            default: null,
        },
        minHeight: {
            required: false,
            default: null,
        },
        minWidth: {
            required: false,
            default: null,
        },
        ratio: String,
    },
    data: function () {
        return {
            canDrop: null,
            uploading: false,
            topImage: this.image,
            error: null,
        };
    },
    methods: {
        handleDragEnter(e) {
            this.canDrop = e.dataTransfer.items[0].type.match(/image\/.+/);
        },
        handleDragStop() {
            this.canDrop = null;
        },
        handleDrop(e) {
            if (this.canDrop) {
                this.initiateUpload(e.dataTransfer.files[0]);
            }

            this.handleDragStop();
        },
        handleInput(e) {
            this.initiateUpload(e.target.files[0]);
        },
        initiateUpload(image) {
            if (typeof image === 'undefined') {
                return;
            }

            let data = new FormData();

            data.append('file', image);
            data.append('type', 'image');

            this.uploading = true;
            this.error = null;

            files.upload(data)
                .then(result => {
                    this.uploading = false;

                    this.queuedFile = result;

                    let reader = new FileReader();
                    reader.onload = e => {
                        this.loadNewImage(e.target.result);
                    };
                    reader.readAsDataURL(image);


                    //Check for required dimensions on image
                    if (this.minWidth || this.minHeight) {
                        //Initiate the JavaScript Image object.
                        let img = new Image();
                        //Read the contents of Image File.
                        img.src = window.URL.createObjectURL(image);
                        //Validate the File Height and Width.
                        img.onload = () => {
                            if (img.width < this.minWidth || img.height < this.minHeight) {

                                this.error = this.$t('validation.image_min_dimensions', {
                                    minWidth: this.minWidth,
                                    minHeight: this.minHeight,
                                    uploadedWidth: img.width,
                                    uploadedHeight: img.height,
                                });
                            }
                        };
                    }

                    this.$emit('success', result);
                    this.$emit('input', result.id);
                })
                .catch(error => {
                    this.uploading = false;
                });
        },
        clearImage() {
            this.topImage = null;
            this.$emit('input', null);
            this.$emit('clear', null);
        },
        loadNewImage(image) {
            this.topImage = image;
        },
    },
};
</script>

<style>
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
    transition-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
}

.fade-quick-enter-active, .fade-quick-leave-active {
    transition: opacity .2s;
    transition-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
}

.fade-quick-enter, .fade-quick-leave-to, .fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
